import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { PageContainer } from "../../../components/PageContainer";
import { Poll } from "../../../__generated__/graphql";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { FaqLabel } from "../../edition/edit/form";
import { ZonedDateTimePicker } from "../../../components/ZonedDateTimePicker";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { DELETE_POLL, GET_POLL, UPSERT_POLL } from "../../post/graphql";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { confirmAlert } from "react-confirm-alert";

export interface IArtistLink {
  type: "artist" | "vendor";
  linkedItemName: string;
  linkedId: string;
  description: string;
}

const schema = yup
  .object({
    id: yup.string(),
    title: yup.string().required(),
    expiryDate: yup.string().required(),
    choices: yup.number().required(),
    pollValues: yup
      .array()
      .of(
        yup.object({
          name: yup.string().required(),
        })
      )
      .required(),
  })
  .required();

// .object({
//   name: yup.string().required(),
//   description: yup.string().required(),
//   showcase: yup.boolean(),
//   imageUrl: yup.string().required(),
//   socials: yup.object({
//     facebook: yup.string().url().optional(),
//     tiktok: yup.string().url().optional(),
//     twitter: yup.string().url().optional(),
//     instagram: yup.string().url().optional(),
//     website: yup.string().url().optional(),
//     bandcamp: yup.string().url().optional(),
//   }),
// })
// .required();

const PollLink = () => {
  const { id } = useParams();

  const [getPoll, { data, loading }] = useLazyQuery(GET_POLL);

  useEffect(() => {
    if (!id || id === "new") return;
    getPoll({ variables: { id: id } });
  }, [id]);

  if (loading) return null;

  // @ts-ignore
  return <PollForm poll={data?.poll} />;
};

export interface IPollForm {
  poll: Poll | undefined | null;
}

const PollForm = ({ poll }: IPollForm) => {
  const navigate = useNavigate();
  const newDate = dayjs(new Date())
    .add(1, "week")
    .set("hours", 0)
    .set("minutes", 0)
    .set("seconds", 0)
    .set("milliseconds", 0);
  const defaultDate = poll?.expiryDate ? dayjs(poll?.expiryDate) : newDate;
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, dirtyFields },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: poll?.id ?? undefined,
      title: poll?.title ?? "",
      choices: poll?.choices ?? 3,
      // @ts-ignore
      expiryDate: defaultDate,
      pollValues:
        poll?.values.map((item) => ({
          name: item,
        })) ?? [],
    },
  });

  // useEffect(() => {
  //   reset({
  //     id: poll.id,
  //     title: poll.title,
  //     // @ts-ignore
  //     expiryDate: new Date(poll.expiryDate),
  //     // @ts-ignore
  //     values: poll.values ?? []
  //   });

  // }, [links])

  const { fields, replace, append, remove, swap, move, insert } = useFieldArray(
    {
      control, // control props comes from useForm (optional: if you are using FormContext)
      name: "pollValues",
      keyName: "",
      // name: "pollValues", // unique name for your Field Array
    }
  );

  const [upsertPoll, { loading: updateLoading, error }] = useMutation(
    UPSERT_POLL,
    {
      onCompleted: () => {
        toast("Poll updated");
      },
      onError: (error) => {
        toast("An error has occured");
        console.log("error", error);
      },
      refetchQueries: [GET_POLL],
    }
  );
  const [deletePoll] = useMutation(DELETE_POLL, {
    onCompleted: () => {
      toast("Poll deleted");
      navigate("/polls");
    },
    onError: (error) => {
      toast("An error has occured");
      console.log("error", error);
    },
    refetchQueries: [GET_POLL],
  });

  const onSubmit = (form: {
    title: string;
    choices: number;
    expiryDate: string;
    pollValues: { name: string }[];
  }) => {
    upsertPoll({
      variables: {
        id: poll?.id ?? undefined,
        title: form.title,
        choices: form.choices,
        expiryDate: form.expiryDate,
        values: form.pollValues.map((item) => item.name),
      },
    });

    // const withoutDeleted = form.links.filter(
    //   (item) => !forDeletion.includes(item.linkedId)
    // );
    // const ready = withoutDeleted.reduce((acc, next) => {
    //   // @ts-ignore
    //   acc[next.linkedId] = next;
    //   return acc;
    // }, {});
    // updateBand({
    //   variables: {
    //     id: band.id,
    //     links: ready,
    //   },
    // });
  };
  const handleDelete = () => {
    if (!poll?.id) return;

    confirmAlert({
      title: "Delete this poll?",
      message: "Are you sure? this can't be undone",
      buttons: [
        {
          label: "Yes",
          onClick: () => deletePoll({ variables: { pollId: poll?.id } }),
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  return (
    <PageContainer title="Poll">
      {/* @ts-ignore */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          fullWidth
          id="outlined-basic"
          label="title"
          variant="outlined"
          defaultValue={poll?.title}
          error={!!errors.title}
          {...register(`title`)}
        />
        <TextField
          fullWidth
          id="outlined-basic"
          label="Number of choices"
          variant="outlined"
          defaultValue={poll?.choices}
          error={!!errors.choices}
          {...register(`choices`)}
        />
        {/* <TextField
          fullWidth
          id="outlined-basic"
          label="Expiry date"
          variant="outlined"
          defaultValue={poll?.expiryDate}
          error={!!errors.expiryDate}
          {...register(`expiryDate`)}
        /> */}
        <Controller
          control={control}
          name="expiryDate"
          render={({ field: { onChange } }) => (
            <ZonedDateTimePicker
              format="YYYY/MM/DD HH:mm"
              ampm={false}
              label="Poll expiry date"
              defaultValue={defaultDate}
              // @ts-ignore
              onChange={onChange} // send value to hook form
            >
              <FaqLabel style={{ marginTop: 4 }}>The Poll expiry date</FaqLabel>
            </ZonedDateTimePicker>
          )}
        />
        <Button
          onClick={() => append({ name: "" })}
          color="success"
          variant="contained"
          style={{ height: "100%" }}
        >
          Add New
        </Button>
        <TableContainer component={Paper} style={{ minHeight: 500, maxHeight: 500, overflow: 'scroll' }}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="left">Poll option</TableCell>
              </TableRow>
            </TableHead>
            <TableBody style={{overflow: 'scroll'}}>
              {fields.map((row, index) => (
                <TableRow
                  key={row.name}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  style={{ overflow: "visible" }}
                >
                  <TableCell align="left">
                    <TextField
                      fullWidth
                      id="outlined-basic"
                      label="title"
                      variant="outlined"
                      defaultValue={row.name}
                      error={!!errors.pollValues?.[index]}
                      {...register(`pollValues.${index}.name`)}
                    />
                  </TableCell>
                  <TableCell align="left">
                    <Button
                      onClick={() => {
                        remove(index);
                      }}
                      color="error"
                      variant="contained"
                      style={{
                        // marginTop: 18,
                        marginBottom: 20,
                        // marginRight: 8,
                      }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Button
          type="submit"
          variant="contained"
          style={{ marginTop: 18, marginBottom: 18 }}
        >
          Save
        </Button>
        {poll?.id && (
          <Button
            onClick={handleDelete}
            variant="contained"
            style={{
              marginTop: 18,
              marginBottom: 18,
              marginLeft: 8,
              backgroundColor: "red",
            }}
          >
            Delete
          </Button>
        )}
      </form>
    </PageContainer>
  );
};

export default PollLink;
